import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, Users, Euro, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { API } from '../App';

const TrainingsPage = () => {
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);

  // Stage Cascade Provence Studio - Formation complète
  const demoTrainings = [
    {
      id: 'stage-cascade-complet',
      title: 'Stage Cascade - Formation Complète',
      description: 'Formation aux fondamentaux de la cascade physique et de l\'action cinéma. Acquérir les bases techniques, physiques et artistiques : chuter, donner et recevoir des coups, utiliser des armes factices, cascades en hauteur et au feu, mise en situation filmée.',
      price: 1800,
      date: '2026-03-10',
      duration: '5 jours (35h)',
      max_participants: 12,
      current_participants: 0,
      category: 'Formation Complète',
      image_url: 'https://images.unsplash.com/photo-1510177087648-3ae3fa149848?w=800',
    },
  ];

  const fetchTrainings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/trainings`);
      setTrainings(response.data.length > 0 ? response.data : demoTrainings);
    } catch (error) {
      console.error('Error fetching trainings:', error);
      setTrainings(demoTrainings);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrainings();
  }, [fetchTrainings]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Header */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <p className="font-['Barlow'] text-[#FACC15] uppercase tracking-[0.3em] text-sm mb-4">
              Apprenez avec les pros
            </p>
            <h1 
              className="font-['Teko'] text-6xl md:text-8xl text-white mb-6"
              data-testid="trainings-title"
            >
              FORMATIONS
            </h1>
            <p className="font-['Barlow'] text-gray-400 text-lg max-w-2xl mx-auto">
              Des formations professionnelles pour apprendre les techniques de cascade 
              avec des coordinateurs expérimentés.
            </p>
            <div className="mt-6 inline-flex items-center gap-3 bg-[#FACC15]/10 border border-[#FACC15]/30 px-6 py-3">
              <span className="font-['Teko'] text-xl text-[#FACC15]">AFDAS</span>
              <span className="font-['Barlow'] text-white text-sm">Formations éligibles au financement AFDAS</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trainings List */}
      <section className="pb-24">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner" />
            </div>
          ) : (
            <div className="space-y-6">
              {trainings.map((training, index) => (
                <motion.div
                  key={training.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="group bg-[#09090b] border border-white/10 hover:border-[#FACC15]/30 transition-colors overflow-hidden"
                  data-testid={`training-card-${training.id}`}
                >
                  <div className="flex flex-col md:flex-row">
                    {/* Image */}
                    <div className="md:w-80 h-48 md:h-auto flex-shrink-0 overflow-hidden">
                      <img
                        src={training.image_url || 'https://images.unsplash.com/photo-1510177087648-3ae3fa149848?w=800'}
                        alt={training.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>

                    {/* Content */}
                    <div className="flex-1 p-6 md:p-8 flex flex-col">
                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-4 mb-4">
                          <div>
                            <span className="font-['Barlow'] text-[#FACC15] text-xs uppercase tracking-wider">
                              {training.category}
                            </span>
                            <h3 className="font-['Teko'] text-3xl md:text-4xl text-white mt-1">
                              {training.title}
                            </h3>
                          </div>
                          <div className="text-right">
                            <span className="font-['Teko'] text-4xl text-[#FACC15]">
                              {training.price}€
                            </span>
                          </div>
                        </div>

                        <p className="font-['Barlow'] text-gray-400 mb-6 leading-relaxed">
                          {training.description}
                        </p>

                        <div className="flex flex-wrap gap-6 text-sm">
                          <div className="flex items-center gap-2 text-gray-400">
                            <Calendar size={16} className="text-[#FACC15]" />
                            <span className="font-['Barlow']">{formatDate(training.date)}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-400">
                            <Clock size={16} className="text-[#FACC15]" />
                            <span className="font-['Barlow']">{training.duration}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-400">
                            <Users size={16} className="text-[#FACC15]" />
                            <span className="font-['Barlow']">
                              {training.current_participants}/{training.max_participants} places
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="mt-6 pt-6 border-t border-white/10 flex items-center justify-between">
                        <div className="w-full max-w-xs bg-[#18181b] h-2 overflow-hidden">
                          <div 
                            className="h-full bg-[#FACC15]"
                            style={{ 
                              width: `${(training.current_participants / training.max_participants) * 100}%` 
                            }}
                          />
                        </div>
                        <Link to={`/trainings/${training.id}`}>
                          <Button 
                            className="bg-[#FACC15] text-black font-bold uppercase tracking-wider hover:bg-[#EAB308] transition-colors ml-4"
                            disabled={training.current_participants >= training.max_participants}
                            data-testid={`register-btn-${training.id}`}
                          >
                            {training.current_participants >= training.max_participants 
                              ? 'Complet' 
                              : 'S\'inscrire'
                            }
                            <ChevronRight size={18} className="ml-1" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {trainings.length === 0 && !loading && (
            <div className="text-center py-20">
              <p className="font-['Barlow'] text-gray-400 text-lg">
                Aucune formation disponible pour le moment.
              </p>
              <p className="font-['Barlow'] text-gray-500 mt-2">
                Revenez bientôt pour découvrir nos prochaines sessions !
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#09090b] border-t border-white/10">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <h2 className="font-['Teko'] text-4xl text-white mb-4">
            FORMATION SUR MESURE ?
          </h2>
          <p className="font-['Barlow'] text-gray-400 mb-6 max-w-xl mx-auto">
            Vous avez des besoins spécifiques ? Nous proposons également des formations 
            personnalisées pour les productions et les équipes.
          </p>
          <Link to="/contact">
            <Button 
              variant="outline"
              className="border-[#FACC15] text-[#FACC15] font-bold uppercase tracking-wider hover:bg-[#FACC15] hover:text-black"
              data-testid="custom-training-btn"
            >
              Nous contacter
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default TrainingsPage;
